<template>
    <v-card flat>
        <v-card-text class="page--status_reports">
          <h2 class="page-title">Status Report (Summary)</h2>
          <p class="text-xs-center"><em>{{ lastChecked }}</em></p>
          <v-layout class="status_reports mt-4" v-if="!!$roomService.rooms">
            <v-flex>
              <v-container class="top-container top-container--full" grid-list-md>
                <v-layout row wrap justify-space-around>
                  <v-flex md4 sm12 xs12>
                    <v-text-field
                      append-icon="search"
                      label="Rooms"
                      single-line
                      hide-details
                      v-model="rawSearchTerms"
                      :loading="$reportService.loading"
                    ></v-text-field>
                  </v-flex>

                  <v-flex class="sm-pt-4" md4 sm12 xs12 v-if="showDepartmentUnit">
                    <v-select
                        class="select__room_org_units"
                        :items="roomOrgUnits"
                        v-model="filter.roomOrgUnit"
                        item-text="label"
                        item-value="value"
                        label="Department / Unit"
                        :loading="$reportService.loading"
                        autocomplete
                        clearable
                    ></v-select>
                  </v-flex>

                  <v-flex md4 sm12 xs12>
                    <v-select
                        class="select__room_owners"
                        :items="roomOwners"
                        v-model="filter.roomOwner"
                        item-text="label"
                        item-value="value"
                        label="Owner"
                        :loading="$reportService.loading"
                        autocomplete
                        clearable
                    ></v-select>
                  </v-flex>

                  <v-flex md4 sm12 xs12>
                    <v-select
                        class="select__campusRegions"
                        :items="roomCampusRegions"
                        v-model="filter.roomCampusRegion"
                        item-text="label"
                        item-value="value"
                        label="Campus / Region"
                        :loading="$reportService.loading"
                        autocomplete
                        clearable
                    ></v-select>
                  </v-flex>

                  <v-flex md4 sm3 xs6>
                    <v-menu
                      v-model="startDatePicker"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      :nudge-right="40"
                      full-width
                      min-width="290px"
                    >
                      <v-text-field
                        slot="activator"
                        v-model="displayDates.startDate"
                        label="Start Date"
                        prepend-icon="event"
                        readonly
                      ></v-text-field>
                      <v-date-picker
                        v-model="filter.startDate"
                        :allowed-dates="{ min: '1970-01-01', max: filter.endDate }"
                        @input="startDatePicker = false"
                        class="select__date"
                        label="Start Date"
                      ></v-date-picker>
                    </v-menu>
                  </v-flex>

                  <v-flex md4 sm3 xs6>
                    <v-menu
                      v-model="endDatePicker"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      :nudge-right="40"
                      full-width
                      min-width="290px"
                    >
                      <v-text-field
                        slot="activator"
                        v-model="displayDates.endDate"
                        label="End Date"
                        prepend-icon="event"
                        readonly
                      ></v-text-field>
                      <v-date-picker
                        v-model="filter.endDate"
                        :allowed-dates="{ min: filter.startDate, max: utcString(now) }"
                        @input="endDatePicker = false"

                        class="select__date"
                        label="End Date"
                      ></v-date-picker>
                    </v-menu>
                  </v-flex>

                  <v-flex md3 sm6 xs12 class="text-lg-right">
                    <v-checkbox
                      class="mx-auto"
                      label="Only Show Overdue Rooms"
                      v-model="filter.onlyOverdue"
                      color="primary"
                    ></v-checkbox>
                  </v-flex>

                  <v-flex v-if="viewerIsRoomOwner" md2 xs12 offset-md2 class="flex-flex text-xs-center">
                    <v-btn
                      class="btn__my_rooms"
                      color="primary"
                      @click="filter.roomOwner = $auth.user().uid"
                    >
                      <span class="btn__text">Show My Rooms</span>
                    </v-btn>
                  </v-flex>

                  <v-flex md2 xs12 class="flex-flex text-xs-center" :class="[viewerIsRoomOwner ? 'offset-md3' : 'offset-md7']">
                    <v-btn
                      class="btn__download"
                      color="primary"
                      @click="downloadCSV"
                    >
                      <span class="btn__text">Download Report</span>
                    </v-btn>
                  </v-flex>

                </v-layout>
              </v-container>

              <v-data-table
                :search="filter.searchTerms"
                :headers="reportsTableHeaders"
                :items="reportsTableItems"
                :pagination.sync="pagination"
                :custom-filter="customSearchFilter"
                :loading="$reportService.loading"
                item-key="room_id"
                ref='reportsTable'
                must-sort
                hide-actions
                no-results-text="Sorry, either there are no results available that match the search criteria, or data is not currently available."
                no-data-text="Sorry, either there are no results available that match the search criteria, or data is not currently available."
              >
                <template slot="headers" slot-scope="props">
                  <tr class="primary">
                    <template v-for="header in props.headers">
                      <th
                        :key="header.text"
                        v-if="!header.hide"
                        role="columnheader"
                        :aria-label="header.text"
                        :aria-sort="header.sortable ? 'sortable' : ''"
                        @click="changeSort(header)"
                        :class="[
                          (header.sortable || header.align !== 'left') ? 'px-1' : 'pr-1',
                          'white--text column column--' + header.value,
                          'text-xs-' + (header.align || 'center'),
                          header.value === pagination.sortBy ? 'active' : '',
                          header.sortable ? 'sortable' : '',
                          pagination.descending ? 'desc' : 'asc'
                        ]"
                        @mouseenter="header.tooltipOpen = true"
                        @mouseleave="header.tooltipOpen = false"
                      >
                        <v-icon v-if="header.sortable" color="white">arrow_upward</v-icon>
                        {{ header.text }}
                        <v-tooltip
                          v-if="header.tooltip"
                          bottom
                          :activator="'.column--' + header.value"
                          v-model="header.tooltipOpen"
                        >
                          <span>{{ header.tooltip.text }}</span>
                        </v-tooltip>
                      </th>
                    </template>
                  </tr>
                </template>
                <template slot="items" slot-scope="props">
                  <td class="text-xs-left column--room_report px-1 text-xs-center">
                    <v-layout row>
                      <v-flex xs6>
                        <v-btn
                          :class="'ma-0'"
                          color="grey"
                          flat
                          depressed
                          small
                          fab
                          :disabled="roomReportDialog"
                          @click="openRoomReport(props.item.room_id)"
                        >
                          <v-icon>assignment</v-icon>
                        </v-btn>
                      </v-flex>
                      <v-flex xs6>
                        <v-btn
                          :class="'ma-0'"
                          color="grey"
                          flat
                          depressed
                          small
                          fab
                          @click="startReport(props.item.room_id)"
                        >
                          <v-icon>edit</v-icon>
                        </v-btn>
                      </v-flex>
                    </v-layout>
                  </td>
                  <td class="text-xs-left column--room_label pr-1">
                    {{ props.item.label }}
                  </td>
                  <td class="text-xs-left column--org_unit pr-1" v-if="showDepartmentUnit">
                    <div
                      v-if="!!props.item.org_unit.line1"
                      class="org_unit__line1"
                    >
                      {{ props.item.org_unit.line1 }}
                    </div>
                    <div
                      v-if="!!props.item.org_unit.line2"
                      class="org_unit__line2"
                    >
                      {{ props.item.org_unit.line2 }}
                    </div>
                  </td>
                  <td class="text-xs-left column--campus pr-1">
                    {{ props.item.campus }}
                  </td>
                  <td class="text-xs-left column--campus_region pr-1">
                    {{ props.item.campus_region }}
                  </td>
                  <td
                    :class="[
                      'text-xs-right',
                      'column--room_minimum_frequency',
                      'px-1',
                      props.item.check_status_color_class
                    ]"
                  >
                    <v-tooltip right v-if="!!props.item.support_level">
                      <div slot="activator">{{ props.item.minimum_frequency }} Days</div>
                      <span>{{ props.item.support_level }}</span>
                    </v-tooltip>
                    <span v-else>{{ props.item.minimum_frequency }} Days</span>
                  </td>
                  <td class="text-xs-left column--room_last_checked pr-1">{{ props.item.last_checked | date }}</td>
                  <td class="text-xs-right px-1 column--room_incident_count">
                    {{ props.item.incidentCount }}
                  </td>
                  <td class="text-xs-right px-1 column--room_check_count">
                    {{ props.item.checkCount }}
                  </td>
                  <td class="text-xs-right px-1 column--room_expected_check_count">
                    {{ props.item.minimumCheckCount }}
                  </td>
                  <td class="text-xs-right px-1 column--room_check_count">
                    {{ props.item.checkPercent | sub100Percent }}
                    <span style="display:none;">{{ props.item.checkPercent }}</span>
                  </td>
                </template>
              </v-data-table>
              <v-dialog
                v-model="roomReportDialog"
                persistent
                scrollable
                max-width="90%"
                content-class="dialog--room_status_reports"
              >
                <v-card>
                  <v-toolbar dark color="primary">
                    <v-toolbar-title>{{ roomReportLabel }}</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn icon @click.native="closeRoomReport()" dark>
                      <v-icon>close</v-icon>
                    </v-btn>
                  </v-toolbar>
                  <v-data-table
                    :headers="roomReportTableHeaders"
                    :items="roomReportTableItems"
                    :loading="$reportService.loading"
                    item-key="report_id"
                    hide-actions
                    no-results-text="No reports are available for this room."
                    no-data-text="No reports are available for this room."
                  >
                    <template slot="items" slot-scope="props">
                      <td class="text-xs-center column--room_status" >
                        <v-icon
                          :color="getStatusIcon(props.item.room_status).color"
                        >{{ getStatusIcon(props.item.room_status).icon }}</v-icon>
                      </td>
                      <td class="text-xs-left column--date">{{ props.item.timestamp | date }}</td>
                      <td class="text-xs-left column--notes">
                        <ul class="feature_bullet_list">
                          <template v-for="(feature, index) in props.item.inventory">
                            <li
                              :key="index"
                              :class="'feature_bullet_list_item feature_bullet_list_item--' + getStatusIcon(props.item[feature.featureId]).color"
                            >
                              <strong class="feature_bullet__name">
                                {{ feature.featureName }}
                              </strong>
                              <em
                                v-if="props.item[feature.featureId + '_issue_description']"
                                class="feature_bullet__notes"
                              >
                                {{ props.item[feature.featureId + '_issue_description'] }}
                              </em>
                            </li>
                          </template>
                        </ul>
                      </td>
                    </template>
                  </v-data-table>
                </v-card>
              </v-dialog>
            </v-flex>
          </v-layout>
        </v-card-text>
    </v-card>
</template>

<script>
// TODO: Make whole room clickable to trigger roomReport
import moment from 'moment-business-days'

export default {
  name: 'SuccinctReports',
  data() {
    let now = Date.now()
    return {
      rawSearchTerms: '',
      searchTimeout: -1,
      debouncePeriod: 500,
      console,
      searchKeys: ['active_status', 'campus', 'campus_region', 'customer_or_owner', 'email_primary_owner', 'email_secondary_owner', 'label', 'netid_primary_owner', 'primary_contact_name', 'room_type', 'secondary_contact_name', 'secondary_customer_or_owner'],
      filter: {
        searchTerms: '',
        roomOwner: '',
        roomOrgUnit: '',
        roomCampusRegion: '',
        onlyOverdue: false,
        startDate: moment(now).subtract(1, 'y').format('YYYY-MM-DD'),
        endDate: this.utcString(now)
      },
      pagination: {
        sortBy: 'last_checked',
        descending: true,
        rowsPerPage: -1,
        page: 1
      },
      baseReportsTableHeaders: [
        {
          text: 'Actions',
          align: 'center',
          sortable: false
        }, {
          text: 'Room',
          align: 'left',
          value: 'label'
        }, {
          text: 'Dept. / Unit',
          align: 'left',
          value: 'org_unit.value',
          sortable: true
        }, {
          text: 'Campus',
          align: 'left',
          value: 'campus',
          sortable: true
        }, {
          text: 'Region',
          align: 'left',
          value: 'campus_region',
          sortable: true
        }, {
          text: 'Frequency',
          align: 'right',
          value: 'minimum_frequency',
          sortable: true
        }, {
          text: 'Last Checked',
          align: 'left',
          value: 'last_checked'
        }, {
          text: 'Incidents',
          align: 'right',
          value: 'incidentCount',
          sortable: true
        }, {
          text: 'Checks',
          align: 'right',
          value: 'checkCount',
          sortable: true
        }, {
          text: 'Min Checks*',
          align: 'right',
          value: 'minimumCheckCount',
          sortable: true,
          tooltipOpen: false,
          tooltip: {
            text: 'Calculated from the oldest check in the date range until now',
          }
        }, {
          text: 'Check %',
          align: 'right',
          value: 'checkPercent',
          sortable: true
        }
      ],
      startDatePicker: false,
      endDatePicker: false,
      roomReportDialog: false,
      roomReportRoomId: null,
      now: now,
    }
  },
  props: {
    teamId: {
      type: String,
      required: true
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    startReport(roomId) {
      const teamId = this.teamId;
      this.$router.push({
        path: `/${teamId}/report/${roomId}`
      });
    },
    loadData() {
      this.$reportService.refreshData(this.teamId);
      this.$roomService.refreshData(this.teamId);
    },
    customSearchFilter (items, search, filter) {
      search = search.toString().toLowerCase();

      if(this.filter.onlyOverdue) {
        items = items.filter(it => it.check_overdue);
      }

      // Apply organizational owner filter
      if(this.filter.roomOrgUnit) {
        items = items.filter(it =>
          it.org_unit.line1 === this.filter.roomOrgUnit || it.org_unit.value === this.filter.roomOrgUnit
        );
      }

      // Apply room owner filter
      if(this.filter.roomOwner) {
        items = items.filter(it =>
          it.netid_primary_owner === this.filter.roomOwner || it.netid_secondary_owner === this.filter.roomOwner
        );
      }

      // Apply region filter
      if(this.filter.roomCampusRegion) {
        let [campus, region] = this.filter.roomCampusRegion.split(' - ')
        // debugger;
        items = items.filter(it => it.campus === campus);
        if (region) items = items.filter(it => it.campus_region === region);
      }
      
      // Apply keyword search filters
      if(search) {
        // Split the search terms up by space, and search for all of them
        var searchTerms = search.split(" ").filter(term => term)
        items = items.filter(i => searchTerms.every(term => this.searchKeys.some(j => filter(i[j], term))))
      }

      return items
    },
    changeSort(header) {
      if(header.sortable !== false) {
        var column = header.value;
        if (this.pagination.sortBy === column) {
          this.pagination.descending = !this.pagination.descending
        } else {
          this.pagination.sortBy = column
          this.pagination.descending = false
        }
      }
    },
    openRoomReport(roomId) {
      this.roomReportRoomId = roomId;
      this.roomReportDialog = true;
    },
    closeRoomReport() {
      // this.roomReportRoomId = null; // TODO: Maybe re-enable this?
      this.roomReportDialog = false;
    },
    getStatusIcon(status) {
      if(status === true) {
        return {
          color: 'success',
          icon: 'check_circle'
        }
      } else if(status === false) {
        return {
          color: 'warning',
          icon: 'warning'
        }
      } else {
        return {
          color: 'grey lighten-1',
          icon: 'do_not_disturb_on'
        }
      }
    },
    utcString(time) {
      return moment(time).format('YYYY-MM-DD')
    },
    finishSearchDebounce () {
      this.filter.searchTerms = this.rawSearchTerms
    },
    downloadCSV () {
      let noActionHeaders = this.reportsTableHeaders.filter(th => th.text !== "Actions")
      let headers = noActionHeaders
        .filter(th => !th.hide)
        .map(th => th.text.replace('*',''))
      let data = this.filteredReportsTableItems.map(room => [
          room.label,
          room.org_unit.value.replace(/^ - | - $/, ''),
          room.campus,
          room.campus_region,
          room.minimum_frequency + ' Days',
          this.dateFilter(room.last_checked),
          room.incidentCount,
          room.checkCount,
          room.minimumCheckCount,
          this.sub100PercentFilter(room.checkPercent),
        ].filter((_, i) => !noActionHeaders[i].hide )
          .map(val => val && val.includes && val.includes('"') ? `"${val.replace(/"/g,'\"')}"` : val)
      )
      let csvText = headers.join(',') + '\n' + data.map(row => row.join(',')).join('\n')
      
      let filter = this.filter
      function errDefault (f, def = null) { try { return f() } catch (e) { return def } }
      let filename = [
        'roomready',
        filter.searchTerms && 'search',
        filter.roomOwner,
        errDefault(() => filter.roomOrgUnit.match(/^\S+/)[0]),
        errDefault(() => filter.roomOrgUnit.match(/ - (^\S+)/)[1]),
        filter.startDate,
        filter.endDate
      ].filter(item => item).join('_') + '.csv'
      let url = 'data:text/csv,' + filename
      this.download(filename, csvText, 'text/csv')
    },
    download(filename, text, mimeType = 'text/plaincharset=utf-8') {
      let el = document.createElement('a')
      el.href = 'data:' + mimeType + ',' + encodeURIComponent(text)
      el.download = filename
      el.click()
    },
    dateFilter(timestamp) {
      return timestamp ? moment(timestamp, 'x').format('MM/DD/YYYY h:mma') : 'Never';
    },
    sub100PercentFilter(number) {
      return isNaN(number) ? undefined : number >= 1 ? '100%' : Math.round(number * 100) + '%'
    }
  },
  computed: {
    reportsTableHeaders () {
      this.baseReportsTableHeaders.find(h => h.value === 'org_unit.value').hide = !this.showDepartmentUnit
      return this.baseReportsTableHeaders
    },
    reportsForTimePeriod () {
      return this.$reportService.reports.filter(it => it.date_only_timestamp &&
        moment(it.date_only_timestamp).isBetween(this.moments.startDate, this.moments.endDate, 'day', '[]')
      )
    },
    reportsByRoom () {
      let reports = this.reportsForTimePeriod
      let result = {}
      reports.forEach(report => {
        if (!result[report.room_id]) result[report.room_id] = []
        !result[report.room_id].push(report)
      })
      return result
    },
    reportsTableItems() {
      let rooms = this.$roomService.rooms.slice()
      let reports = this.reportsForTimePeriod
      if(! (rooms && reports)) return []

      rooms.forEach(room => {
        // Add data for department / unit column
        room.org_unit = {
          value: null,
          line1: room.customer_or_owner || '',
          line2: room.secondary_customer_or_owner || ''
        }
        room.org_unit.value = `${room.org_unit.line1} - ${room.org_unit.line2}`
      })
      // Don't populate other fields if reports haven't loaded
      if (!reports.length) return rooms
      ;(async () => rooms.forEach((room, i) => {
        // Check for and mark whether rooms are overdue
        var business_days_since_check = moment(room.last_checked, 'x').endOf('day').businessDiff(this.moments.now.endOf('day'));
        room.check_status_color_class = '';
        if(business_days_since_check > room.minimum_frequency || !room.last_checked) {
          room.check_status_color_class = 'error--text';
          room.check_overdue = true;
        } else {
          room.check_overdue = false;
        }

        // Insert report and incident counts into the room object
        let relevantReports = this.reportsByRoom[room.room_id] || []
        room.checkCount = relevantReports.length
        room.incidentCount = 0
        relevantReports.forEach(report => {
          room.incidentCount += !report.room_status
        })
        // Determine if room is being reported on enough
        let reportingStart = relevantReports.reduce((start, it) => {
          return it.timestamp < start.timestamp ? it : start
        }, {timestamp: Infinity})
        let reportingDuration = moment(reportingStart.timestamp, 'x').endOf('day')
          .businessDiff(this.moments.now.endOf('day'))
        room.minimumCheckCount = Math.floor(reportingDuration / room.minimum_frequency)
        if (isNaN(room.minimumCheckCount)) room.minimumCheckCount = 0
        room.checkPercent = room.checkCount / room.minimumCheckCount
      }))()
      return rooms;
    },
    filteredReportsTableItems () {
      return this.$refs.reportsTable.filteredItems
    },
    roomReportLabel() {
      if(this.roomReportRoomId) {
        return this.$roomService.getRoom(this.roomReportRoomId).label
      }
      return ''
    },
    roomReportTableHeaders() {
      if(this.roomReportRoomId) {
        var headers = [
          {
            text: "Status",
            align: "center",
            sortable: false,
            value: "room_status",
          },
          {
            text: "Date",
            align: "left",
            sortable: false,
            value: "timestamp",
          },
          {
            text: "Notes",
            align: "left",
            sortable: false,
            value: "room_status",
          }
        ];
        return headers;
      }
      return [];
    },
    roomReportTableItems() {
      if(this.roomReportRoomId) {
        return this.$reportService.reports.filter(it => it.room_id == this.roomReportRoomId).reverse().map(report => {
          report.inventory = this.$roomService.getRoomInventory(report.room_id).filter(feature => report[feature.featureId] === false || report[feature.featureId + '_issue_description']);
          if(!report.inventory.length) {
            report.clean_report = true;
            report.clean_report_issue_description = '';
            report.inventory.push({
              featureId: 'clean_report',
              featureName: 'No Issues Reported',
            });
          }
          return report;
        });
      }
      return [];
    },
    roomOwners() {
      var rooms = this.$roomService.rooms;
      var owners = Array.from(new Set(
        rooms.filter(it => !!it.netid_primary_owner && !!it.primary_contact_name).map(it => {
          return {
            value: it.netid_primary_owner,
            label: it.primary_contact_name
          }
        }).concat(rooms.filter(it => !!it.netid_secondary_owner && !!it.secondary_contact_name).map(it => {
          return {
            value: it.netid_secondary_owner,
            label: it.secondary_contact_name
          }
        }))
      ));
      return owners
    },
    roomCampusRegions() {
      var rooms = this.$roomService.rooms;
      var campusRegions = new Set()
      rooms.forEach(it => {
        let { campus, campus_region } = it
        if (campus) campusRegions.add(campus)
        if (campus_region) campusRegions.add(campus + " - " + campus_region)
      })
      return Array.from(campusRegions).sort()
    },
    roomOrgUnits() {
      var rooms = this.$roomService.rooms;
      var orgUnits = Array.from(new Set(
        rooms
          .filter(it => it.customer_or_owner)
          .map(it => it.customer_or_owner)
          .concat(
            rooms
              .filter(it => it.customer_or_owner && it.secondary_customer_or_owner)
              .map(it => it.customer_or_owner + " - " + it.secondary_customer_or_owner)
          )
      )).sort();
      return orgUnits
    },
    viewerIsRoomOwner() {
      return !!this.roomOwners.filter(it => it.value == this.$auth.user().uid).length
    },
    showDepartmentUnit() {
      return !!this.roomOrgUnits.length
    },
    lastChecked() {
      var prefix = 'Last Updated: ';
      if(this.$roomService.rooms) {
        var lastRoomsChecked = this.$roomService.rooms.filter(it => !!it.last_checked).map(it => it.last_checked).sort().reverse();
        if(lastRoomsChecked.length) {
          return prefix + moment(lastRoomsChecked[0], 'x').format('M/D/YY') + ' at ' + moment(lastRoomsChecked[0], 'x').format('h:mma')
        }
        return prefix + 'Never';
      }
      return 'Loading...';
    },
    displayDates() {
      return {
        startDate: this.moments.startDate.format('MMM D YYYY'),
        endDate: this.moments.endDate.format('MMM D YYYY')
      }
    },
    moments() {
      return {
        startDate: moment(this.filter.startDate),
        endDate: moment(this.filter.endDate),
        now: moment(this.now),
      }
    }
  },
  watch: {
    rawSearchTerms () {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(this.finishSearchDebounce, this.debouncePeriod)
    }
  },
  filters: {
    date(timestamp) {
      return timestamp ? moment(timestamp, 'x').format('MM/DD/YYYY h:mma') : 'Never';
    },
    sub100Percent(number) {
      return isNaN(number) ? undefined : number >= 1 ? '100%' : Math.round(number * 100) + '%'
    }
  }
};
</script>

<style>
td.column--room_report {
  width: 96px;
}
td.column--room_label {
  min-width: 140px;
}
td.column--org_unit {
  min-width: 90px;
  max-width: 250px;
}
td.column--room_minimum_frequency {
  min-width: 50px;
}
td.column--room_last_checked {
  min-width: 105px;
}
td.column--room_check_count {
  min-width: 50px;
}
td.column--room_incident_count {
  min-width: 70px;
}

div.flex {
  max-width: 100%;
}

.top-container {
  max-width: 600px;
}

.top-container--full {
  max-width: 100%;
}

.flex-flex {
  display: flex;
  flex-direction: column;
}
.btn__download, .btn__my_rooms {
  flex-shrink: 1;
  flex-grow: 1;
  overflow: auto;
}

@media (max-width: 959px) {
  .sm-pt-4 {
    padding-top: 16px !important;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/SuccinctReports.vue